import React, { useEffect, useState } from "react";
import { bindActionCreators } from "redux";
import { connect, useSelector, useDispatch } from "react-redux";
import { getMessage, changeLanguage } from "CONFIG/i18n";

// Components
import StartSupportCenterSession from "COMPONENTS/SupportCenter/StartSupportCenterSession/StartSupportCenterSession";

// Actions
import { sessionActions, leaveSessionAction, organisationAction } from "CONFIG/ActionFactory";

// Utilities
import { getURLParameter } from "UTILS/Utility";

// Constants
import { ERROR, CONFIG, SESSION_STATUS, USER_ADMISSION_STATUS } from "UTILS/Constants";
import { socketMessage } from "WEBSOCKET/constants";

// Redux
import { getSelectedLanguage } from "REDUX/Selector/LanguageSelector";
import {
  selectSessionKey,
  selectSessionStatus,
  selectSessionName,
  selectIsCalling,
  selectIsSessionEnded,
  selectIsDisconnectedWhileCalling,
} from "REDUX/Selector/SessionSelector";
import { selectAdmissionStatus } from "REDUX/Selector/UserSelector";
import setLanguageAction from "COMPONENTS/CommonComponents/ChangeLanguage/setLanguageAction";

// Websocket
import { sendWebsocketMessage } from "WEBSOCKET/WebsocketHandler";

// Assets
import img_header_logo from "ASSETS/Images/img_header_logo.webp";

const StartSupportCenterSessionContainer = (props) => {
  const [isOffline, setIsOffline] = useState(!window.navigator.onLine);
  const [errorInfo, setErrorInfo] = useState(null);
  const [isCancelClicked, setIsCancelClicked] = useState(false);
  const selectedLanguage = useSelector(getSelectedLanguage);
  const sessionKey = useSelector(selectSessionKey);
  const sessionStatus = useSelector(selectSessionStatus);
  const userSessionName = useSelector(selectSessionName);
  const isCalling = useSelector(selectIsCalling);
  const isSessionEnded = useSelector(selectIsSessionEnded);
  const isDisconnectedWhileCalling = useSelector(selectIsDisconnectedWhileCalling);
  const admissionStatus = useSelector(selectAdmissionStatus);
  const dispatch = useDispatch();
  const handleChangeLanguage = (language) => {dispatch(setLanguageAction(language))};
  let description = [];
  let shouldShowLoader = false;
  let sessionName = "";
  let shouldShowCancel = false;
  let errorMessage = "";

  const handleCancelClick = () => {
    if (!isCancelClicked) {
      const sessionCancelPayload = {
        data: {
          category: socketMessage.category.SESSION,
          action: socketMessage.subCategories.SESSION.CANCEL_SESSION,
          sessionKey: sessionKey
        }
      };
      props.sendWebsocketMessage(
        socketMessage.events.MESSAGE,
        sessionCancelPayload
      );
      setIsCancelClicked(true);
    }
  };

  useEffect(() => {
    // Detects that you have been offline at least once.
    window.addEventListener('offline', () => { setIsOffline(true) });

    // Clear session information on reload.
    leaveSessionAction.localSessionAllDataCleanUp();

    const licenseKey = getURLParameter("licenseKey");
    const language = getURLParameter("language");
    const name = getURLParameter("name");
    const requirement = getURLParameter("requirement");
    const branchName = getURLParameter("branchName");
    const branchCode = getURLParameter("branchCode");
    const contractUrl = getURLParameter("contractUrl");

    let sessionNameForSend = "";
    if (branchName) {
      sessionNameForSend += branchName;
    }
    if (branchCode) {
      if (sessionNameForSend) {
        sessionNameForSend += " ";
      }
      sessionNameForSend += branchCode;
    }
    if (name) {
      if (sessionNameForSend) {
        sessionNameForSend += "/";
      }
      sessionNameForSend += name;
    }

    let metadata = null;
    if (contractUrl) {
      let contractInfo;
      try {
        contractInfo = JSON.parse(contractUrl);
      } catch (e) {
        contractInfo = contractUrl;
      }
      metadata = {
        contractInfo: contractInfo
      }
    }

    if (window.navigator.onLine) {
      sessionActions.createSessionSupportCenterAction(licenseKey, sessionNameForSend, requirement, language, metadata).then(
        data => {
          const logoUrl = data.data.data.logoUrl ? data.data.data.logoUrl : img_header_logo;
          organisationAction.setLogoUrl(logoUrl);
          sessionActions.joinSessionAction(data.data.data.sessionKey, props.uriIdentifier).then(
            response => {
              const sessionJoinPayload = {
                data: {
                  category: socketMessage.category.SESSION,
                  action: socketMessage.subCategories.SESSION.JOIN_SESSION_REQUEST,
                  userName: name,
                  sessionKey: response.data.data.sessionKey,
                  token: response.data.data.token
                }
              };
              props.sendWebsocketMessage(
                socketMessage.events.MESSAGE,
                sessionJoinPayload
              );
            },
            error => {
              setErrorInfo(error);
            }
          );
        },
        error => {
          setErrorInfo(error);
          const logoUrl = error.details?.logoUrl ? error.details.logoUrl : img_header_logo;
          organisationAction.setLogoUrl(logoUrl);
        }
      );
    }

    const defaultLanguage = (language === "ja" || language === "en") ? language : selectedLanguage;
    changeLanguage(defaultLanguage);
    handleChangeLanguage(defaultLanguage);
  }, []);

  useEffect(() => {
    if (sessionStatus === SESSION_STATUS.ACTIVE) {
      props.history.push(CONFIG.path.dashboard);
    }
  }, [sessionStatus]);

  if (errorInfo) {
    switch (errorInfo.code) {
      case ERROR.SUPPORT_CENTER_OUTSIDE_OF_BUSINESS_HOUR:
        if (Array.isArray(errorInfo.details.exceptionDaysOfWeek) && errorInfo.details.exceptionDaysOfWeek.length > 0) {
          const exceptionDaysOfWeekText = errorInfo.details.exceptionDaysOfWeek.map((day) => getMessage(day)).join(" ");
          if (errorInfo.details.isReceptionDisabledOnExceptionDays) {
            description = [
              getMessage("SUPPORT_CENTER_SORRY"),
              getMessage("SUPPORT_CENTER_OUT_OF_RECEPTION_HOURS"),
              getMessage("SUPPORT_CENTER_CLOSE_TAB"),
              "",
              getMessage("SUPPORT_CENTER_RECEPTION_HOURS"),
              `【${errorInfo.details.skillName}】`,
              `${getMessage("REGULAR")}:　　　　　${errorInfo.details.startTime}～${errorInfo.details.endTime}`,
              `${exceptionDaysOfWeekText}:　　　　　${getMessage("NO_RECEPTION")}`,
            ];
          } else {
            description = [
              getMessage("SUPPORT_CENTER_SORRY"),
              getMessage("SUPPORT_CENTER_OUT_OF_RECEPTION_HOURS"),
              getMessage("SUPPORT_CENTER_CLOSE_TAB"),
              "",
              getMessage("SUPPORT_CENTER_RECEPTION_HOURS"),
              `【${errorInfo.details.skillName}】`,
              `${getMessage("REGULAR")}:　　　　　${errorInfo.details.startTime}～${errorInfo.details.endTime}`,
              `${exceptionDaysOfWeekText}:　　　　　
                  ${errorInfo.details.exceptionStartTime}～${errorInfo.details.exceptionEndTime}`,
            ];
          }
        } else {
          description = [
            getMessage("SUPPORT_CENTER_SORRY"),
            getMessage("SUPPORT_CENTER_OUT_OF_RECEPTION_HOURS"),
            getMessage("SUPPORT_CENTER_CLOSE_TAB"),
            "",
            getMessage("SUPPORT_CENTER_RECEPTION_HOURS"),
            `【${errorInfo.details.skillName}】　　　　　${errorInfo.details.startTime}～${errorInfo.details.endTime}`
          ];
        }
        break;
      case ERROR.SUPPORT_CENTER_ALL_OPERATOR_IS_ABSENCE:
        description = [
          getMessage("SUPPORT_CENTER_CLOSE_AND_RECONNECT")
        ];
        break;
      default:
        errorMessage = getMessage(errorInfo.code);
        break;
    }
  } else if (isSessionEnded) {
    description = [
      getMessage("AGENT_HAS_ENDED_THE_SESSION"),
      getMessage("SUPPORT_CENTER_CLOSE_TAB")
    ];
  } else if (admissionStatus === USER_ADMISSION_STATUS.REJECTED) {
    description = [
      getMessage("USER_REJECTED_MESSAGE"),
      getMessage("SUPPORT_CENTER_CLOSE_TAB")
    ];
  } else if (sessionStatus === SESSION_STATUS.ABANDONED) {
    description = [
      getMessage("SUPPORT_CENTER_CANCEL_SESSION"),
      getMessage("SUPPORT_CENTER_CLOSE_TAB")
    ];
  } else if (isOffline || isDisconnectedWhileCalling) {
    description = [
      getMessage("NETWORK_ERROR")
    ];
  } else if (userSessionName) {
    shouldShowLoader = true;
    sessionName = userSessionName;
    if (isCalling) {
      description = [
        getMessage("SUPPORT_CENTER_NOW_CONNECTING"),
        getMessage("SUPPORT_CENTER_WAIT_A_MOMENT")
      ];
    } else {
      description = [
        getMessage("SUPPORT_CENTER_SORRY"),
        getMessage("SUPPORT_CENTER_OPERATOR_IS_NOT_AVAILABLE"),
        getMessage("SUPPORT_CENTER_PLEASE_WAIT"),
      ];
      shouldShowCancel = true;
    }
  }

  return (
    <>
      <StartSupportCenterSession
        language={selectedLanguage}
        onChangeLanguage={handleChangeLanguage}
        description={description}
        shouldShowLoader={shouldShowLoader}
        sessionName={sessionName}
        shouldShowCancel={shouldShowCancel}
        onCancelClick={handleCancelClick}
        errorMessage={errorMessage}
      />
    </>
  );
};

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      sendWebsocketMessage: sendWebsocketMessage,
    },
    dispatch
  );
};

export default connect(
  null,
  mapDispatchToProps
)(StartSupportCenterSessionContainer);
